<template>
  <div>
    <transaction
      :transaction="invoice"
      verbose-name="Facture"
      verbose-name-plural="Factures"
      ressource="invoices"
      entity="invoice"
      icon="bx bx-file"
      transaction-line-name="invoicelines"
      services="Invoice"
      :transaction-line-form="invoiceLineForm"
      :choices="choices"
      :edit="invoice && invoice.status === 'pro_format' && scope()"
      :lines-loaded="linesLoaded"
      :lines="lines"
    >
      <template v-if="invoice" #profile-items>
        <h-c-profile-item
          v-if="invoice.linked_quotation && scope()"
          name="Créée à partir du devis"
          :text="invoice.linked_quotation.number"
          class="cursor-pointer"
          @click.native="goToQuotation(invoice.linked_quotation.id)"
        ></h-c-profile-item>
        <h-c-profile-item
          v-if="invoice.linked_purchase_order && scope()"
          :name="
            $route.params.sales === 'true' ? 'Créée à partir du bon de commande' : 'Créée à partir de l\'ordre d\'achat'
          "
          :text="invoice.linked_purchase_order.number"
          class="cursor-pointer"
          @click.native="goToPurchaseOrder(invoice.linked_purchase_order.id)"
        ></h-c-profile-item>
        <h-c-profile-item
          v-if="invoice.linked_delivery_note && scope()"
          name="Créée à partir du bon de livraison"
          :text="invoice.linked_delivery_note.number"
          class="cursor-pointer"
          @click.native="goToDeliveryNote(invoice.linked_delivery_note.id)"
        ></h-c-profile-item>
        <h-c-profile-item
          v-if="invoice.installments && scope()"
          name="Paiement en plusieurs fois"
          text="Autorisé"
          class="cursor-pointer"
          icon="bx bx-money"
          @click.native="goToDeliveryNote(invoice.linked_delivery_note.id)"
        ></h-c-profile-item>
        <h-c-profile-item name="Reste à payer" :text="`${invoice.left_to_pay}€`"></h-c-profile-item>
      </template>
      <template v-if="invoice" #buttons>
        <h-c-action
          v-if="invoice.status === 'accepted'"
          ref="paymentCard"
          :add="scope() && invoice.left_to_pay > 0 && invoice.status !== 'pro_format'"
          title="Paiement(s)"
          :height="130"
          class="mb-3"
          :loaded-post="loadedPost"
          @clickCancel="$refs.paymentCard.manageFormClose()"
          @clickSubmit="submitInvoicePayment"
        >
          <template #content>
            <b-row v-for="invoicePaiement in invoice.invoice_payments" :key="invoicePaiement.id">
              <b-col cols="3" class="d-flex justify-content-center"> {{ Math.abs(invoicePaiement.amount) }} €</b-col>
              <b-col cols="1" class="d-flex justify-content-center"> -</b-col>
              <b-col cols="3" class="d-flex justify-content-center">
                {{ invoicePaiement.provider | formatProvider }}</b-col
              >
              <b-col cols="1" class="d-flex justify-content-center"> - </b-col>
              <b-col cols="3" class="d-flex justify-content-center">
                {{ invoicePaiement.payment_date | formatDate }}
              </b-col>
              <b-col cols="1" class="d-flex justify-content-center"> -</b-col>
              <b-col cols="3" class="d-flex justify-content-center">
                {{ invoicePaiement.note }}
              </b-col>
            </b-row>
          </template>
          <template #form>
            <h-f-fields
              v-model="invoicePaymentForm.amount"
              label="Montant"
              input-type="unit"
              unit="€"
              :read-only="!invoice.installments"
              :submitted="submitted"
              :invalid="$v.invoicePaymentForm.amount.$error"
              :decimal="!$v.invoicePaymentForm.amount.decimal"
              :max-value="!$v.invoicePaymentForm.amount.maxValue"
              :max-value-required="invoice.left_to_pay"
              :min-value="!$v.invoicePaymentForm.amount.maxValue"
              :min-value-required="0"
              :required="!$v.invoicePaymentForm.amount.required"
            ></h-f-fields>
            <h-f-fields
              v-model="invoicePaymentForm.payment_date"
              input-type="date"
              label="Date de paiement *"
              :submitted="submitted"
              :invalid="$v.invoicePaymentForm.payment_date.$error"
              :required="!$v.invoicePaymentForm.payment_date.required"
            ></h-f-fields>
            <h-f-fields v-model="invoicePaymentForm.note" label="Nom du paiement" />
          </template>
        </h-c-action>
        <div v-if="invoice.status !== 'canceled'">
          <h-c-action :height="invoice.credits.length ? 75 : 90" title="AVOIRS" :add="false" class="mb-3">
            <template v-if="invoice.credits.length" #content>
              <p>
                Nombre d'avoirs : <span class="text-primary">{{ invoice.credit_details.number }}</span>
              </p>
              <p>
                Montant inclus dans des avoirs : <span class="text-primary">{{ invoice.credit_details.include }}€</span>
              </p>
            </template>
            <template v-else #content>
              <p>
                Aucun avoir n'a encore été généré. Pour en créer un, clique sur le bouton "Créer un avoir" dans la liste
                des actions ci-dessous.
              </p>
            </template>
          </h-c-action>
          <h-c-action :height="invoice.refunds.length ? 75 : 90" title="REMBOURSEMENTS" :add="false" class="mb-3">
            <template v-if="invoice.refunds.length" #content>
              <p>
                Nombre de remboursements : <span class="text-primary">{{ invoice.refund_details.number }}</span>
              </p>
              <p>
                Montant inclus dans des remboursements :
                <span class="text-primary">{{ invoice.refund_details.include }}€</span>
              </p>
            </template>
            <template v-else #content>
              <p>
                Aucun remboursement n'a encore été généré. Pour en créer un, clique sur le bouton "Créer un
                remboursement" dans la liste des actions ci-dessous.
              </p>
            </template>
          </h-c-action>
        </div>
        <div class="mt-3 d-flex flex-column justify-content-center align-items-center">
          <h-b-action :text="formatStatus.text" :color="formatStatus.color" positive class="mb-2 w-75" />
          <h-b-action
            v-if="invoice.status !== 'canceled'"
            :text="formatPaymentStatus.text"
            :color="formatPaymentStatus.color"
            positive
            class="mb-2 w-75"
          ></h-b-action>
          <h-b-action
            v-if="invoice.status === 'pro_format' && scope()"
            text="Valider la facture"
            :positive="false"
            color="success"
            class="w-75 mb-2"
            @click.native="editInvoiceStatus('accepted')"
          ></h-b-action>
          <h-b-action
            v-if="invoice.status === 'accepted' && scope()"
            text="Remettre en pro format"
            :positive="false"
            color="warning"
            class="w-75 mb-2"
            @click.native="editInvoiceStatus('pro_format')"
          ></h-b-action>
          <h-b-action
            v-if="invoice.status === 'pro_format' && scope()"
            text="Annuler la facture"
            :positive="false"
            color="danger"
            class="w-75 mb-2"
            @click.native="editInvoiceStatus('canceled')"
          ></h-b-action>
          <!-- <h-b-action
              v-if="invoice.status !== 'canceled'"
              text="Créer un retour produit"
              :positive="false"
              class="w-75 mb-2"
              @click.native="createProductReturn"
          ></h-b-action> -->
          <h-b-action
            v-if="invoice.left_to_pay > 0 && invoice.status !== 'canceled' && invoice.status !== 'pro_format'"
            text="Rapprochement bancaire"
            :positive="false"
            class="w-75 mb-2"
            @click.native="openReconciliationModal"
          ></h-b-action>
          <h-b-action
            v-if="
              invoice.left_to_pay > 0 && invoice.status !== 'canceled' && invoice.status !== 'pro_format' && scope()
            "
            text="Créer un remboursement"
            :positive="false"
            class="w-75 mb-2"
            @click.native="createRefund"
          ></h-b-action>
          <h-b-action
            v-if="
              invoice.status !== 'canceled' && invoice.status !== 'pro_format' && scope()
            "
            text="Créer un avoir"
            :positive="false"
            class="w-75 mb-2"
            @click.native="createCredit"
          ></h-b-action>
          <h-b-action
            v-if="
              invoice.left_to_pay > 0 && invoice.status !== 'canceled' && invoice.status !== 'pro_format' && scope()
            "
            text="Créditer un avoir"
            :positive="false"
            class="w-75 mb-2"
            @click.native="openModal"
          ></h-b-action>
          <h-b-action
            v-if="invoice.status !== 'canceled' && scope()"
            text="Visualiser la facture"
            :positive="false"
            class="w-75 mb-2"
            @click.native="goToPublicInvoice"
          ></h-b-action>
          <h-b-action
            v-if="invoice.status !== 'canceled' && scope()"
            text="Voir la version format X"
            :positive="false"
            class="w-75 mb-2"
            @click.native="getPdfVersion"
          ></h-b-action>
          <h-b-action
            v-if="!invoice.is_sales && invoice.status === 'accepted' && invoice.payment_status !== 'paid' && scope()"
            text="Effectuer un paiement"
            :positive="false"
            class="w-75 mb-2"
            @click.native="goToSendPayment()"
          ></h-b-action>
        </div>
      </template>
    </transaction>

    <b-modal v-model="showModal" size="lg" title="Liste des avoirs de retour produits" centered hide-footer>
      <!-- <b-row>
        <b-col cols=6 />
        <b-col cols=6>
          <h-f-design-search-field v-model="searchInput" />
        </b-col>
      </b-row> -->
      <h-t-table v-if="loadedCredits" class="mt-3">
        <template #table-head>
          <h-t-table-header> NUMÉRO</h-t-table-header>
          <h-t-table-header> CLIENT</h-t-table-header>
          <h-t-table-header> LIEU DE STOCKAGE</h-t-table-header>
          <h-t-table-header> QUANTITÉS</h-t-table-header>
          <h-t-table-header> TOTAL</h-t-table-header>
          <h-t-table-header> STATUT</h-t-table-header>
          <h-t-table-header-action />
        </template>
        <template #table-body>
          <tr v-for="(item, index) in credits" :key="item.id">
            <h-t-table-element>{{ item.number }}</h-t-table-element>
            <h-t-table-element>{{ item.organization_object.company_name }}</h-t-table-element>
            <h-t-table-element>{{ item.stockage_area_object.name }}</h-t-table-element>
            <h-t-table-element>{{ item.total_quantity }}</h-t-table-element>
            <h-t-table-element>{{ item.total_price }} €</h-t-table-element>
            <h-t-table-element>
              <h-e-rectangle
                class="tab-btn"
                color-full
                :title="formatCreditStatus(item.status)"
                :bg-color="formatColor(item.status)"
              />
            </h-t-table-element>
            <h-t-table-element-action>
              <h-b-action text="Ajouter" @click.native="addCredits(index)" />
            </h-t-table-element-action>
          </tr>
        </template>
      </h-t-table>
    </b-modal>
    <b-modal
      v-model="showReconciliationModal"
      title="Transactions suggérées"
      header-class="border-0 justify-content-start"
      footer-class="border-0 justify-content-start"
      centered
      :hide-footer="!eligibleTransactions.length"
      @ok="submitReconciliation"
    >
      <b-form-checkbox-group
        v-if="eligibleTransactions.length"
        id="checkbox-group-2"
        v-model="selectedAccountTransactions"
        name="flavour-2"
      >
        <ul class="list-unstyled overflow-auto" style="height: 40vh">
          <li
            v-for="transaction in eligibleTransactions"
            :key="transaction.id"
            class="d-flex border m-1 p-2 align-items-center rounded"
            :class="{ 'bg-soft-semi-light': transaction.disabled }"
          >
            <b-form-checkbox :value="transaction" :disabled="transaction.disabled" />
            <div class="d-flex flex-grow-1 align-items-center">
              <div class="mr-3"><i class="p-2 rounded-circle bg-soft-primary text-primary mdi mdi-bank"></i></div>
              <div class="d-flex flex-grow-1 flex-column" :class="{ 'text-muted': transaction.disabled }">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{{ transaction.clean_description }}</strong>
                  </div>
                  <div
                    class="px-3 py-1 rounded"
                    :class="transaction.amount > 0 ? 'bg-soft-success text-success' : 'bg-soft-danger text-danger'"
                  >
                    {{ transaction.amount | formatValue }}
                  </div>
                </div>
                <div class="d-flex justify-content-between">
                  <div>Compte {{ transaction.account_id }}</div>
                  <div>{{ transaction.date | formatDate }}</div>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </b-form-checkbox-group>
      <div v-else>Pas de transaction</div>
      <template #modal-footer="{ ok, cancel }">
        <div class="d-flex justify-content-end">
          <h-b-action :positive="false" text="Annuler" @click.native="cancel()"></h-b-action>
          <h-b-action :positive="true" class="ml-2" text="Ajouter" @click.native="ok()"></h-b-action>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { Hk } from '@/node-sdk'
import { required, decimal, maxValue, minValue } from 'vuelidate/lib/validators'

import moment from 'moment'
import { confirmModalSuccess } from '@/helpers/swalModal'
import Transaction from '../components/transaction.vue'

const { Invoice, InvoiceLine, InvoicePayment, Credit } = Hk.Crm
const { Transaction: ExternalTransaction } = Hk.Payment

export default {
  name: 'invoice',

  components: {
    Transaction,
  },

  filters: {
    formatDate(date) {
      return moment(date).format('DD/MM/YYYY')
    },

    formatProvider(value) {
      return InvoicePayment.get_provider_choices().find((item) => item.value === value).text
    },
    formatValue(number) {
      return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(number)
    },
  },

  data() {
    return {
      invoice: null,
      invoiceLineForm: null,
      invoicePaymentForm: InvoicePayment.get_form(),
      submitted: false,
      loadedPost: true,
      choices: null,
      showModal: false,
      accountTransactions: [],
      showReconciliationModal: false,
      selectedAccountTransactions: [],
      credits: [],
      loadedCredits: false,
      linesLoaded: false,
      lines: [],
    }
  },

  validations() {
    return {
      invoicePaymentForm: {
        amount: {
          required,
          decimal,
          maxValue: maxValue(this.invoice.left_to_pay),
          minValue: minValue(0),
        },
        payment_date: { required },
      },
    }
  },

  computed: {
    formatStatus() {
      const statusReturn = {}
      statusReturn.text = Invoice.get_status().find((status) => status.value === this.invoice.status).text
      if (this.invoice.status === 'pro_format') {
        statusReturn.color = 'warning'
      } else if (this.invoice.status === 'accepted') {
        statusReturn.color = 'success'
      } else if (this.invoice.status === 'canceled') {
        statusReturn.color = 'danger'
      }
      return statusReturn
    },

    formatPaymentStatus() {
      const statusReturn = {}
      statusReturn.text = Invoice.get_payment_status().find(
        (status) => status.value === this.invoice.payment_status,
      ).text
      if (this.invoice.payment_status === 'partially_paid') {
        statusReturn.color = 'warning'
      } else if (this.invoice.payment_status === 'paid') {
        statusReturn.color = 'success'
      } else if (this.invoice.payment_status === 'unpaid') {
        statusReturn.color = 'danger'
      }
      return statusReturn
    },

    eligibleTransactions() {
      if (!this.accountTransactions) return []
      const reconciliatedTransactions = this.invoice
        ? this.invoice.invoice_payments
            .filter(({ provider }) => provider === 'reconciliation')
            .map(({ provider_id: providerId }) => providerId)
        : []
      return this.accountTransactions
        .filter(({ amount }) => {
          return this.invoice.is_sales ? amount > 0 : amount < 0
        })
        .map((t) => {
          return { ...t, disabled: reconciliatedTransactions.includes(t.id) }
        })
    },
  },

  async mounted() {
    await Invoice.getById(this.$route.params.id, {
      params: {
        expand: [
          'delivery_taxes',
          'linked_quotation',
          'linked_purchase_order',
          'linked_delivery_note',
          'invoice_payments',
          'refunds',
          'credits',
        ],
      },
    }).then(async (invoice) => {
      this.invoice = invoice
      ;[this.lines] = await InvoiceLine.get({
        params: {
          transaction: this.$route.params.id,
          expand: ['taxes', 'label'],
        },
      })
      this.invoice.invoicelines = this.lines
      this.linesLoaded = true
    })
    this.invoiceLineForm = InvoiceLine.get_form()
    this.choices = Invoice[`get_${this.invoice.is_sales ? 'sales' : 'expenses'}_choices`]()
    this.invoicePaymentForm.amount = this.invoice.left_to_pay
  },

  methods: {
    async addCredits(index) {
      const copy = JSON.parse(JSON.stringify(this.credits[index]))
      this.credits.splice(index, 1)
      copy.linked_invoice = this.$route.params.id
      this.invoice.credits = Credit.edit(copy.id, { data: copy })
    },

    async openModal() {
      ;[this.credits] = await Credit.get({ params: { linked_invoice_is_null: true } })
      this.loadedCredits = true
      this.showModal = !this.showModal
    },

    async openReconciliationModal() {
      this.accountTransactions = await ExternalTransaction.get()

      this.showReconciliationModal = !this.showReconciliationModal
    },

    formatColor(status) {
      const colors = {
        pending: 'warning',
        accepted: 'success',
        canceled: 'danger',
      }
      return colors[status]
    },

    formatCreditStatus(value) {
      return Credit.get_status().find((item) => item.value === value).text
    },

    async editInvoiceStatus(status) {
      this.invoice.status = status
      await this.editInvoice()
    },

    async editInvoice() {
      const invoice = { ...this.invoice }
      delete invoice.linked_quotation
      delete invoice.delivery_taxes
      delete invoice.linked_purchase_order
      delete invoice.linked_delivery_note
      await Invoice.patch(invoice.id, { data: invoice })
    },

    async submitInvoicePayment() {
      this.submitted = true
      this.$v.$touch()
      if (!this.$v.$invalid) {
        this.loadedPost = false
        this.invoicePaymentForm.invoice = this.invoice.id
        this.invoicePaymentForm.payment_status = 'completed'
        const invoicePayment = await InvoicePayment.create(this.invoicePaymentForm)
        this.invoice.invoice_payments.push(invoicePayment)
        this.formatLeftToPay()
        this.$refs.paymentCard.manageFormClose()
        this.submitted = false
        this.loadedPost = true
      }
    },

    async submitReconciliation() {
      const totalAmount = Math.abs(
        this.selectedAccountTransactions.reduce((total, { amount }) => {
          return total + amount
        }, 0),
      )
      const exceededAmountAlert =
        totalAmount > this.invoice.left_to_pay ? '\n(Montant supérieur au reste à payer!)' : ''
      const result = await confirmModalSuccess(
        'Rapprocher',
        `Montant total rapproché:${this.$options.filters.formatValue(totalAmount)}${exceededAmountAlert}`,
      )
      if (!result.value) {
        this.openReconciliationModal()
        return
      }
      const promises = this.selectedAccountTransactions.map(async (transaction) => {
        const form = {
          ...InvoicePayment.get_form(),
          note: transaction.clean_description,
          amount: Math.abs(transaction.amount),
          invoice: this.invoice.id,
          payment_status: 'completed',
          provider_id: transaction.id,
          provider: 'reconciliation',
          payment_date: new Date(transaction.date),
        }
        return InvoicePayment.create(form)
      })
      const payments = await Promise.all(promises)
      this.invoice.invoice_payments.push(...payments)
      this.formatLeftToPay()
      this.selectedAccountTransactions = []
    },

    createRefund() {
      this.$router.push({
        name: 'refund-create',
        query: { invoice: this.invoice.id },
      })
    },

    createCredit() {
      this.$router.push({
        name: 'credit-create',
        query: { invoice: this.invoice.id },
      })
    },

    createProductReturn() {
      this.$router.push({
        name: 'product-return-create',
        query: { invoice: this.invoice.id },
      })
    },

    formatLeftToPay() {
      const initialValue = 0
      const totalInvoicePayment = this.invoice.invoice_payments.reduce(
        (prevInvoicePayment, currentInvoicePayment) =>
          currentInvoicePayment.payment_status === 'completed'
            ? prevInvoicePayment + Math.abs(+currentInvoicePayment.amount)
            : prevInvoicePayment,
        initialValue,
      )
      this.invoice.left_to_pay = +this.invoice.total_price - +totalInvoicePayment
      this.invoice.left_to_pay = this.invoice.left_to_pay.toFixed(2)
      if (this.invoice.left_to_pay <= 0) {
        this.invoice.payment_status = 'paid'
      } else if (this.invoice.left_to_pay === this.invoice.total_price) {
        this.invoice.payment_status = 'unpaid'
      } else {
        this.invoice.payment_status = 'partially_paid'
      }
      this.invoicePaymentForm.amount = this.invoice.left_to_pay
    },

    goToQuotation(quotation) {
      this.$router.push({ name: 'quotation', params: { id: quotation, expense: 'false' } })
    },

    goToPurchaseOrder(purchaseOrder) {
      this.$router.push({ name: 'purchase-order', params: { id: purchaseOrder } })
    },

    goToDeliveryNote(DeliveryNote) {
      this.$router.push({ name: 'delivery-note', params: { id: DeliveryNote } })
    },

    goToRefund(refund) {
      this.$router.push({ name: 'refund', params: { id: refund } })
    },

    goToPublicInvoice() {
      this.$router.push({ name: 'invoice-public', params: { company: this.invoice.company, id: this.invoice.id } })
    },

    async getPdfVersion() {
      try {
        const response = await Invoice.getPdf(this.invoice.id)
        if (response.pdf_url) {
          window.open(response.pdf_url, '_blank')
          if (response.generated_now) {
            this.$toast.open({
              message: 'PDF généré avec succès',
              position: 'top-right',
              type: 'success',
            })
          }
        } else {
          this.$toast.open({
            message: 'Erreur lors de la génération du PDF',
            position: 'top-right',
            type: 'error',
          })
        }
      } catch (error) {
        console.error('Erreur lors de la récupération du PDF:', error)
        this.$toast.open({
          message: 'Impossible de récupérer le PDF',
          position: 'top-right',
          type: 'error',
        })
      }
    },

    goToSendPayment() {
      const route = this.$router.resolve({ name: 'invoice-payment', params: { id: this.invoice.id } })
      window.open(route.href, '_blank', 'width=1300,height=1200')
    },

    scope() {
      return (
        (this.$hidderComponent('invoice:update[owner]') &&
          this.invoice.user === JSON.parse(localStorage.getItem('user_object')).uuid) ||
        this.$hidderComponent('invoice:full')
      )
    },
  },
}
</script>

<style scoped>
p {
  margin: 0;
  margin-bottom: 5px;
}
</style>
